import { useEffect, useState } from 'react'
import { AuthFlow, type AuthScreen, captureReturnTo } from './auth/AuthFlow.js'
import { SignedIn } from './auth/SignedIn.js'
import { SearchPage } from './inbox/SearchPage.js'
import { fetchCurrentUserResult, type CurrentUser, type CurrentUserResult } from './auth/api.js'
import { clearQueries } from './data/query-store.js'
import { fetchBookingThread, fetchContactThread } from './data/inbox-api.js'
import { onUnauthorized } from './data/unauthorized.js'
import { type Route, currentRoute, navigate, onRouteChange, parseRoute } from './routing.js'
import { PRODUCT_NAME } from './brand.js'
import './styles/tokens.css'
import './styles/base.css'
import './App.css'
import './inbox/inbox.css'
import './inbox/thread.css'
import './inbox/panel.css'
import './inbox/composer.css'
import './inbox/header.css'
import './inbox/palette.css'
import './inbox/search.css'

/**
 * FOUNDATION shell page (TASKS §11).
 *
 * ┌─ ⛔ THIS IS NOT THE APPLICATION SHELL. IT IS A REVIEW SCAFFOLD. ───────────┐
 * │ Stated first and plainly because it was NOT obvious, and the cost of that  │
 * │ was a login that appeared to fail.                                        │
 * │                                                                            │
 * │ It exists to PROVE the token system works in both themes, and to page      │
 * │ through the auth screens for review. No inbox, no navigation, no layout    │
 * │ primitives, no router.                                                    │
 * │                                                                            │
 * │ §13 produces the information architecture, navigation and wireframes;      │
 * │ INBOX builds the real UI against those. Inventing components here would    │
 * │ pre-empt both and produce work that gets thrown away.                     │
 * │                                                                            │
 * │ ⚠️ WHAT THAT COST ON 2026-07-30. The first real account signed in and       │
 * │ nothing happened. `AuthFlow` took an OPTIONAL `onAuthenticated`, this file  │
 * │ never passed it, and four successful logins returned into `undefined?.()`.  │
 * │ The server logged `login succeeded` four times and issued seven sessions.   │
 * │                                                                            │
 * │ The login was never broken. THERE WAS NOWHERE FOR A SIGNED-IN USER TO BE,   │
 * │ and nothing said so: this comment described what the file was FOR without   │
 * │ saying what it WAS NOT, and a reader reasonably assumed the auth screens    │
 * │ led somewhere.                                                            │
 * │                                                                            │
 * │ `SignedIn` is now the smallest honest somewhere: who you are, and Sign Out. │
 * │ INBOX replaces it. `onAuthenticated` is now REQUIRED, so a future shell     │
 * │ cannot repeat the omission silently.                                       │
 * └────────────────────────────────────────────────────────────────────────────┘
 */

type Theme = 'light' | 'dark'

/**
 * Theme state is component-local and NOT persisted.
 *
 * The build plan makes dark mode a per-user setting, but the Settings UI that
 * owns it is ADMIN-ANALYTICS and there is no preference storage in FOUNDATION.
 * Writing to localStorage here would create a second source of truth that the
 * real implementation would then have to migrate away from — so the toggle
 * proves both themes render and stops there.
 */
function useTheme(): [Theme, () => void] {
  const [theme, setTheme] = useState<Theme>(() =>
    window.matchMedia('(prefers-color-scheme: dark)').matches ? 'dark' : 'light',
  )

  useEffect(() => {
    // tokens.css keys off data-theme on the root element; setting it here is
    // the only thing the toggle actually does.
    document.documentElement.setAttribute('data-theme', theme)
  }, [theme])

  return [theme, () => setTheme((t) => (t === 'light' ? 'dark' : 'light'))]
}

/*
 * ⛔ `SEMANTIC_TOKENS` DELETED 2026-08-18 WITH THE SWATCH GRID IT FED.
 *
 * It listed 15 token names by hand. `tokens.css` defines 44 semantic colour
 * tokens, so the gallery showed roughly a third of them while reading as the
 * complete set - the "an incomplete list of exceptions reads as a complete one"
 * shape this project has recorded, in a page whose whole purpose was showing the
 * token system.
 *
 * ✅ THE TOKENS ARE NOT LESS VISIBLE FOR ITS ABSENCE. `tokens.css` is the single
 * source, and four guards already assert it: check-colors, check-token-references,
 * check-token-ladders and check-theme-symmetry. The gallery asserted nothing they
 * do not, and could go stale where they cannot.
 */

/**
 * Screens reachable from the review switcher below.
 *
 * FOUNDATION has no router (that is §13's information architecture and INBOX's
 * job), so the auth screens are reachable from a temporary switcher rather than
 * from URLs. This exists so all seven can be reviewed and theme-checked without
 * pre-empting the routing decision.
 */
/**
 * The current route, as React state.
 *
 * ⛔ THE PARSING STAYS PURE AND THIS IS THE ONLY REACT IN THE PATH. `routing.ts`
 * has no import from React on purpose, so every routing decision is checkable in
 * the server project without a DOM - the same split as `thread-view.ts` and
 * `connection-presentation.ts`, where the decision is tested exhaustively and
 * the component test only asks whether the answer reached the screen.
 */
function useRoute(): Route {
  const [route, setRoute] = useState<Route>(currentRoute)
  // `onRouteChange` returns its own unsubscribe, so the effect can return it
  // directly. Empty deps: one listener per mount, and `setRoute` is stable.
  useEffect(() => onRouteChange(() => setRoute(currentRoute())), [])
  return route
}

/**
 * ⛔ RESOLVE `/inbox/contacts/<n>` TO THE THREAD IT OPENS. Amir's ruling
 * 2026-09-11, CRM-BUILD item 38.
 *
 * ⚠️ `null` WHILE IT RESOLVES, WHICH IS THE SAME VALUE AS "NO CONVERSATION" AND
 * IS CORRECT HERE RATHER THAN LAZY. The inbox's no-conversation state is the
 * list with an empty right pane, which is exactly what should be on screen for
 * the one network round trip this takes. A distinct "resolving" state would
 * render a spinner over a list that is already useful.
 *
 * ⚠️ AND A STALE ANSWER IS DISCARDED. Navigating from contact 10683 to 10684
 * before the first request returns would otherwise open the first contact's
 * thread under the second contact's URL.
 */
/**
 * ⛔ AND `/inbox/bookings/<n>` RESOLVES THE SAME WAY, THROUGH THE SAME HOOK.
 * Added 2026-09-15 with the board. **A second copy of this would be a second
 * place that turns an identifier into a thread**, which is the defect
 * `conversation-order.ts` was created to end - so the hook takes WHICH KIND of
 * reference rather than being duplicated.
 *
 * ⚠️ The key includes the kind, or navigating from contact 10023 to booking
 * 10023 would reuse the first answer: two different records share the number.
 */
type ThreadRef = { kind: 'contact' | 'booking'; reference: number }

function useResolvedThread(target: ThreadRef | null): { conversationId: string | null } {
  const key = target === null ? '' : `${target.kind}:${target.reference}`
  /**
   * ⚠️ `'unresolved'` RATHER THAN A NUL SENTINEL, AND THAT IS NOT A STYLE
   * CHOICE. The first version used a NUL escape to mean "no answer yet", which
   * wrote a literal NUL byte into this file - **and `file` then reports it as
   * DATA, so every grep-based check in the repo skips it silently**, including
   * both em-dash checks, which go on reporting clean.
   *
   * ⛔ I HIT THE RECORDED TRAP AND WORKED AROUND ITS SYMPTOM WITHOUT SEEING IT:
   * `grep SignedIn App.tsx` returned nothing for several minutes and I used a
   * different tool instead of asking why. `no-nul-bytes-in-tracked-text.test.ts`
   * caught it, which is the guard doing exactly what it was written for.
   * `a-nul-byte-makes-a-source-file-invisible-to-every-grep`.
   */
  const [resolved, setResolved] = useState<{ key: string; conversationId: string | null }>({
    key: 'unresolved',
    conversationId: null,
  })
  useEffect(() => {
    if (target === null) return
    let current = true
    void (target.kind === 'booking'
      ? fetchBookingThread(target.reference)
      : fetchContactThread(target.reference))
      .then((r) => {
        if (current) setResolved({ key, conversationId: r.conversationId })
      })
      .catch(() => {
        // ⚠️ SWALLOWED DELIBERATELY: `getJson` already reports a 401 through
        // `reportUnauthorized`, and every other failure leaves the inbox on its
        // empty-pane state, which is what an unresolvable contact should show.
        if (current) setResolved({ key, conversationId: null })
      })
    return () => {
      current = false
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [key])
  return { conversationId: resolved.key === key ? resolved.conversationId : null }
}

/**
 * A session that ended while somebody was using it.
 *
 * ┌─ ⛔ WHY THIS CARRIES A USER ID AND NOT JUST A LOCATION ─────────────────────┐
 * │ `returnTo` restores a URL after signing in. **The person who signs in is    │
 * │ not necessarily the person who was signed out** - a shared machine, or an   │
 * │ admin taking over a browser - and restoring one agent's location into       │
 * │ another agent's session is handing the second person the first one's        │
 * │ position in the product.                                                    │
 * │                                                                            │
 * │ ✅ IT WOULD NOT LEAK THE CONVERSATION. `getThread` joins the scope inside    │
 * │ the query, so a restored id the new session may not see returns zero rows - │
 * │ identical to one that does not exist. **The URL is a request, never a       │
 * │ grant**, and nothing on this side of the wire can widen it.                 │
 * │                                                                            │
 * │ ⚠️ WHAT IT WOULD LEAK IS THE ID ITSELF, in the address bar, to whoever is   │
 * │ now at the keyboard. That is small - they could press Back and see the same │
 * │ - and it is free to not do, so it is not done.                              │
 * └────────────────────────────────────────────────────────────────────────────┘
 */
type ExpiredSession = { returnTo: string; userId: string }

const AUTH_SCREENS: Array<{ id: AuthScreen; label: string }> = [
  { id: 'signIn', label: 'Sign In' },
  { id: 'twoFactorSetup', label: 'Two Factor Setup' },
  { id: 'sessionExpired', label: 'Session Expired' },
  { id: 'passwordResetRequest', label: 'Password Reset Request' },
  { id: 'passwordResetConfirm', label: 'Password Reset Confirm' },
]

export function App(): React.JSX.Element {
  const [theme, toggleTheme] = useTheme()
  const [authScreen, setAuthScreen] = useState<AuthScreen | null>(null)

  /**
   * `undefined` = not asked yet, `null` = asked and nobody is signed in.
   *
   * The three states are kept apart deliberately: rendering the signed-out
   * scaffold while the answer is still in flight would flash the wrong screen at
   * every signed-in load, which is the same third-outcome rule the backend
   * applies to an ambiguous send.
   */
  /**
   * ⛔ `undefined` = NOT ASKED YET. Otherwise one of three answers, and the
   * third is the one that was missing until 2026-08-09: **the server did not
   * respond**, which is not "nobody is signed in". See `fetchCurrentUserResult`.
   */
  const [session, setSession] = useState<CurrentUserResult | undefined>(undefined)
  const [expired, setExpired] = useState<ExpiredSession | null>(null)
  const route = useRoute()
  /** ⛔ One resolver for both identifier forms. See `useResolvedThread`. */
  const contactThread = useResolvedThread(
    route.name !== 'inbox'
      ? null
      : 'contactReference' in route
        ? { kind: 'contact', reference: route.contactReference }
        : 'bookingReference' in route
          ? { kind: 'booking', reference: route.bookingReference }
          : null,
  )

  /**
   * ┌─ ⛔ `/` REDIRECTS TO `/inbox`. THE FOUNDATION SCAFFOLD IS GONE. ───────────┐
   * │ Removed 2026-08-18. `/` was not a route: `parseRoute` returned            │
   * │ `elsewhere`, and the fallthrough rendered FOUNDATION's design-token review │
   * │ page - **on the deployed product, as the first thing any visitor saw.**    │
   * │                                                                          │
   * │ ⚠️ IT HAD ALREADY COST AN HOUR TWICE. The block that used to sit here      │
   * │ opened: *"THIS IS NOT THE APPLICATION SHELL. IT IS A REVIEW SCAFFOLD.     │
   * │ Stated first and plainly because it was NOT obvious, and the cost of that │
   * │ was a login that appeared to fail."* It happened again on 2026-08-18, to  │
   * │ the person who wrote that sentence.                                       │
   * │                                                                          │
   * │ ✅ A REDIRECT RATHER THAN A REPLACEMENT PAGE. Signed in, `/inbox` renders  │
   * │ the inbox; signed out, the branch below renders the auth flow. There is    │
   * │ no third thing for `/` to be, which is why this is one line and not a new  │
   * │ screen.                                                                   │
   * │                                                                          │
   * │ ⚠️ `replace: true` - `/` must not sit in history, or Back from the inbox   │
   * │ lands on a path that immediately redirects forward again.                 │
   * └────────────────────────────────────────────────────────────────────────────┘
   */
  useEffect(() => {
    if (route.name === 'elsewhere') {
      navigate({ name: 'inbox', conversationId: null }, { replace: true })
    }
  }, [route])

  // The signed-in user, or null. ⚠️ `unreachable` collapses to null HERE on
  // purpose - every branch below that asks "is somebody signed in" gets the
  // honest "not as far as we know" - and the render checks `unreachable` FIRST,
  // so no branch that would show the sign-in form is ever reached with it.
  const user: CurrentUser | null = session?.kind === 'signedIn' ? session.user : null

  useEffect(() => {
    void fetchCurrentUserResult().then(setSession)
  }, [])

  /**
   * ┌─ ⛔ THE FIRST CALLER `captureReturnTo()` HAS EVER HAD ──────────────────────┐
   * │ Written in FOUNDATION, exported, documented - *"exported so whatever       │
   * │ detects a 401 can call it"* - and **dead for nine days**. Nothing detected  │
   * │ a 401 because nothing made an authenticated request from a mounted view;   │
   * │ `SessionExpired` was reachable only from the review switcher below.        │
   * │                                                                            │
   * │ ⚠️ THAT IS THE RULE-WITH-NO-CONSUMER SHAPE AT ITS LONGEST INTERVAL IN THIS  │
   * │ CODEBASE. The design was correct and unreachable, which is worse than      │
   * │ wrong: a wrong design gets found the first time it runs, and this one had  │
   * │ a comment naming the exact caller it was waiting for. "Whatever" was INBOX │
   * │ and INBOX is now here.                                                     │
   * │                                                                            │
   * │ Recorded as `returnto-had-no-consumer` in `ops/deferrals.txt`, closed.      │
   * └────────────────────────────────────────────────────────────────────────────┘
   *
   * ── ⛔ A 401 IS NOT AN EXPIRY UNTIL SOMEBODY IS ON SCREEN ────────────────────
   *
   * `data/unauthorized.ts` reports that a request was REFUSED and deliberately
   * decides nothing. The same 401 means two different things, and only this
   * component holds the fact that separates them:
   *
   *   user on screen   the session ENDED while they were working -> SessionExpired
   *   nobody on screen they were never signed in -> the ordinary sign-in path
   *
   * ⚠️ THAT IS CONSEQUENCE 2 FROM `docs/INBOX-SCOPE-AND-EXIT.md`, closed: *"a
   * user logged out after ten days cannot tell that from having just arrived."*
   * It is the not-found-versus-cannot-look collapse living in the UI, and it is
   * fixed by putting the two facts in different places rather than by wording.
   */
  useEffect(
    () =>
      onUnauthorized(() => {
        if (user === undefined || user === null) return

        /**
         * ⛔ `prev ?? ` - THE FIRST 401 WINS, AND A BURST IS ONE EVENT. A
         * reconnect calls `refetchAll()`, so an expiry arrives as one 401 per
         * materialised query in the same tick. Capturing on each would be
         * harmless today and wrong the moment anything navigates in between.
         */
        setExpired((prev) => prev ?? { returnTo: captureReturnTo(), userId: user.id })
        setSession({ kind: 'signedOut' })

        /**
         * The old session's data goes with the old session.
         *
         * ┌─ ⚠️ MUTATION-TESTED 2026-08-08 AND FOUND EQUIVALENT, WHICH IS ────────┐
         * │ RECORDED RATHER THAN TREATED AS A GAP. Removing this line fails       │
         * │ nothing, and the claim I wrote first - *"the next sign-in renders the  │
         * │ PREVIOUS user's list for one frame"* - is **false today**.             │
         * │                                                                      │
         * │ `setUser(null)` unmounts the signed-in subtree, every hook releases    │
         * │ its subscription, and `query-store.ts` deletes an entry the moment its │
         * │ last listener goes - *"release means release"*, decided at length      │
         * │ there. **The unmount already evicts everything this line would.**      │
         * │                                                                      │
         * │ ✅ IT STAYS, ON THE SAME GROUND AS `previewFor`'s equivalent guard:    │
         * │ the two express DIFFERENT REASONS. Eviction-on-release is about        │
         * │ refcounting what is on screen; this is about WHOSE data it is. The     │
         * │ equivalence is a property of today's component tree, not a redundancy  │
         * │ in the rule.                                                          │
         * │                                                                      │
         * │ ⛔ AND THE TREE IS ALREADY SCHEDULED TO CHANGE. Scope item 24's        │
         * │ bootstrap endpoint makes the CURRENT USER part of a registered query - │
         * │ `tests/web-query-registry.test.ts` says so in its exemption for        │
         * │ `auth/api.ts` - and a query materialised outside the signed-in subtree │
         * │ is exactly the case where the unmount stops covering this.             │
         * └──────────────────────────────────────────────────────────────────────┘
         */
        clearQueries()
      }),
    [user],
  )

  /**
   * Sign-in completed. Put them back where they were.
   *
   * ⛔ AND THE URL IS NOT ENOUGH ON ITS OWN, WHICH IS ONLY TRUE BECAUSE OF
   * `pushState`. The session-expired screen renders IN PLACE - nothing
   * redirects, so the address bar still holds the conversation and a naive
   * reading says `returnTo` has no work to do.
   *
   * ⚠️ **THEN BACK MOVES IT.** `navigate()` pushes, so an agent's triage
   * sequence is in the history, and pressing Back on a screen that will not
   * dismiss is the first thing a person tries. The auth screen does not move,
   * the URL walks backwards underneath it, and **nothing on screen changes** -
   * so they press it again. The damage is invisible until they sign in and land
   * three conversations earlier.
   *
   * ✅ THE TWO DECISIONS ARE ONE DECISION. `pushState` created the exposure that
   * makes the capture load-bearing, in the same session. Choosing `replaceState`
   * would have made `returnTo` genuinely redundant and Back useless instead.
   */
  const resume = (to?: string): void => {
    const previous = expired
    void fetchCurrentUserResult().then((r) => {
      setSession(r)
      setExpired(null)
      const u = r.kind === 'signedIn' ? r.user : null
      if (u === null || previous === null) return

      // A different person signed in. See `ExpiredSession` for why the id is
      // carried at all: theirs is the inbox, not the other agent's position in
      // it. Also the fallback for an unparseable capture.
      const restored = to === undefined ? null : parseRoute(to)
      if (previous.userId !== u.id || restored === null || restored.name !== 'inbox') {
        navigate({ name: 'inbox', conversationId: null })
        return
      }
      navigate(restored)
    })
  }

  // Still asking. Render nothing rather than guess.
  if (session === undefined) return <></>

  /**
   * ┌─ ⛔ CHECKED BEFORE EVERY BRANCH THAT COULD SHOW A SIGN-IN FORM ────────────┐
   * │ THE LAST OF THE THREE CONSEQUENCES RECORDED 2026-08-04, closed here.       │
   * │                                                                          │
   * │ `fetchCurrentUser()` returned `null` for a network failure and for "nobody │
   * │ is signed in" alike, so **a cold load on a dead connection showed the      │
   * │ SIGN-IN FORM** - and that is the worst possible wrong answer, because it   │
   * │ invites somebody to type a password that cannot be sent, then tells them   │
   * │ the details are wrong.                                                    │
   * │                                                                          │
   * │ ⚠️ POSITION IS THE WHOLE GUARANTEE, exactly as it is for                   │
   * │ `markListRendered()`. Every branch below assumes a settled answer, so this │
   * │ must come first; moving it lower reintroduces the defect silently.        │
   * │                                                                          │
   * │ ⛔ AND IT OFFERS RETRY RATHER THAN SIGN-IN. The session may be perfectly   │
   * │ valid; nothing about it is known. Sending them to the sign-in form would  │
   * │ be asserting something we did not learn.                                  │
   * └────────────────────────────────────────────────────────────────────────────┘
   */
  if (session.kind === 'unreachable') {
    return (
      <div className="auth">
        <div className="auth__card">
          <p className="auth__brand">{PRODUCT_NAME}</p>
          <h1 className="auth__title">Cannot Reach The Server</h1>
          <div className="auth__message auth__message--error" role="alert">
            <p>
              The connection failed, so we do not know whether you are signed in. Your session
              may still be fine.
            </p>
          </div>
          <button
            type="button"
            className="auth__button"
            onClick={() => {
              setSession(undefined)
              void fetchCurrentUserResult().then(setSession)
            }}
          >
            Try Again
          </button>
        </div>
      </div>
    )
  }

  /**
   * ⛔ THE EXPIRED BRANCH IS ABOVE THE REVIEW SWITCHER AND CARRIES NO REVIEW BAR.
   * A real expiry is not a screen somebody opened to look at, and rendering the
   * development switcher underneath it would put five buttons labelled with
   * other auth screens in front of a user whose session just ended.
   */
  if (expired !== null) {
    return (
      <AuthFlow initial="sessionExpired" returnTo={expired.returnTo} onAuthenticated={resume} />
    )
  }

  /**
   * An explicitly opened review screen wins over the signed-in placeholder, so
   * the auth screens stay reviewable while signed in. That is also the only way
   * to reach Two Factor Setup, which needs a session to work at all.
   */
  /**
   * ⛔ THE SEARCH PAGE IS A ROUTE, NOT A MODE INSIDE THE INBOX. It replaces the
   * inbox rather than overlaying it: a full page with its own URL is what makes
   * a search shareable and survivable across a refresh, which is the same
   * argument that put the open conversation in the path.
   */
  /**
   * ⛔ THE CONTACT RECORD. CRM-BUILD item 31, wave 5's first surface, and the
   * one the mid-phase browser inspection is scheduled against.
   *
   * ⚠️ OPENING A THREAD FROM HERE NAVIGATES TO THE UUID FORM, not to
   * `/inbox/contacts/<n>`. The person clicked a SPECIFIC conversation, which is
   * declaring a thread - and the contact form resolves to the most recent one,
   * so it would open a different conversation from the one they pointed at.
   * Same reasoning that deleted `contactReferenceForConversation`.
   */
  /**
   * ⛔ THE BOARD. CRM-BUILD item 28, and the product's third top-level section.
   *
   * ⚠️ A CARD NAVIGATES TO `/inbox/bookings/<n>`, NOT TO A BOOKING PAGE. Amir's
   * locked decision, confirmed 2026-09-11: **there is no booking page** -
   * clicking a booking anywhere opens the THREAD with that booking in the panel.
   * ⛔ And arriving there writes nothing; the shared pointer moves on an
   * explicit "Make active" and nowhere else. §3.7.
   */
  if (authScreen === null && user !== null && route.name === 'board') {
    return (
      <SignedIn
        user={user}
        onSignedOut={() => setSession({ kind: 'signedOut' })}
        onSetUpTwoFactor={() => setAuthScreen('twoFactorSetup')}
        showBoard
        boardParams={route.params}
        onBoardParamsChange={(next) => navigate({ name: 'board', params: next.toString() })}
        onOpenBooking={(reference) => navigate({ name: 'inbox', bookingReference: reference })}
        /** ⚠️ ADDED WITH THE LIST VIEW. The board's cards never linked a contact;
         *  the list's Contact column does, and it goes to the record rather than
         *  the thread because the column names the person, not the conversation. */
        onOpenContact={(reference) => navigate({ name: 'contact', reference })}
        onOpenSection={(s) =>
          navigate(
            s === 'contacts'
              ? { name: 'contacts', params: '' }
              : s === 'bookings'
                ? { name: 'board', params: '' }
                : { name: 'inbox', conversationId: null },
          )
        }
        conversationId={null}
        onOpenConversation={(id) => navigate({ name: 'inbox', conversationId: id })}
      />
    )
  }

  if (authScreen === null && user !== null && (route.name === 'contact' || route.name === 'contacts')) {
    return (
      <SignedIn
        user={user}
        onSignedOut={() => setSession({ kind: 'signedOut' })}
        onSetUpTwoFactor={() => setAuthScreen('twoFactorSetup')}
        conversationId={null}
        highlightMessageId={null}
        onOpenConversation={(id) => navigate({ name: 'inbox', conversationId: id })}
        {...(route.name === 'contact'
          ? { contactReference: route.reference }
          : { showContactsList: true, listParams: route.name === 'contacts' ? route.params : '' })}
        onListParamsChange={(next) => navigate({ name: 'contacts', params: next.toString() })}
        onOpenContact={(reference) => navigate({ name: 'contact', reference })}
        onOpenSection={(s) =>
          navigate(
            s === 'contacts'
              ? { name: 'contacts', params: '' }
              : s === 'bookings'
                ? { name: 'board', params: '' }
                : { name: 'inbox', conversationId: null },
          )
        }
      />
    )
  }

  if (authScreen === null && user !== null && route.name === 'search') {
    return (
      <SearchPage
        query={route.query}
        onQueryChange={(q) => navigate({ name: 'search', query: q })}
        /**
         * ⛔ BOTH IDS TRAVEL, so the thread can scroll to the message.
         * `Build_Order:490`, and dropping the second opens the conversation at
         * the bottom and leaves somebody to find it.
         */
        onOpenMessage={(conversationId, messageId) =>
          navigate({ name: 'inbox', conversationId, messageId })
        }
        onOpenConversation={(id) => navigate({ name: 'inbox', conversationId: id })}
      />
    )
  }

  if (authScreen === null && user !== null) {
    return (
      <SignedIn
        user={user}
        onSignedOut={() => setSession({ kind: 'signedOut' })}
        onSetUpTwoFactor={() => setAuthScreen('twoFactorSetup')}
        /**
         * ⛔ READ FROM THE ROUTE, WRITTEN BY `navigate`, AND BOTH LIVE HERE.
         * Split across two components, the URL becomes a thing two files each
         * partly own - and the one that reads it stops agreeing with the one
         * that writes it in exactly the case nobody tests: Back.
         */
        /**
         * ⛔ A CONTACT ROUTE CARRIES NO `conversationId` AND RESOLVES TO ONE.
         * `/inbox/contacts/<n>` names the contact, and which of that contact's
         * threads to open is the conversation list's ordering - so the server
         * answers it, and this waits.
         *
         * ⚠️ THE URL IS NOT REWRITTEN TO THE UUID WHEN IT RESOLVES. The contact
         * form IS the canonical address under the decided scheme; rewriting it
         * would undo the swap on every navigation and put the uuid back in every
         * link somebody copies.
         */
        onOpenSection={(s) =>
          navigate(
            s === 'contacts'
              ? { name: 'contacts', params: '' }
              : s === 'bookings'
                ? { name: 'board', params: '' }
                : { name: 'inbox', conversationId: null },
          )
        }
        conversationId={
          route.name === 'inbox'
            ? 'contactReference' in route || 'bookingReference' in route
              ? contactThread.conversationId
              : route.conversationId
            : null
        }
        highlightMessageId={
          route.name === 'inbox' && 'messageId' in route ? route.messageId : null
        }
        onOpenConversation={(id) => navigate({ name: 'inbox', conversationId: id })}
      />
    )
  }

  if (authScreen !== null) {
    return (
      <>
        <AuthFlow
          initial={authScreen}
          /**
           * ⛔ THE PROP WHOSE ABSENCE MADE A SUCCESSFUL LOGIN A NO-OP.
           *
           * Re-reads `/auth/me` rather than trusting the login response, so the
           * signed-in screen renders from the same source every later request
           * uses. A login that succeeds and a session the server recognises are
           * two different claims, and this asks for the second.
           */
          onAuthenticated={() => {
            void fetchCurrentUserResult().then((r) => {
              setSession(r)
              setAuthScreen(null)
            })
          }}
        />
        {/*
          ⛔ DEV ONLY, AND THAT GATE IS NEW ON 2026-08-18. This toolbar cycles the
          auth screens for review. It was reachable only from the FOUNDATION
          scaffold, which is now deleted - so the branch it lives in is reached by
          SIGNED-OUT VISITORS from here on.

          ⚠️ WITHOUT THIS GATE, REMOVING THE SCAFFOLD WOULD HAVE SHOWN A "Review"
          TOOLBAR TO EVERY SIGNED-OUT CUSTOMER - trading one FOUNDATION artefact on
          `/` for a worse one on the sign-in screen. Gated rather than deleted
          because cycling the screens is genuinely useful while building them, and
          `import.meta.env.DEV` is false in every built bundle.
        */}
        {import.meta.env.DEV ? (
        <div className="review-bar">
          <span className="review-bar__label">Review</span>
          {AUTH_SCREENS.map((s) => (
            <button
              key={s.id}
              type="button"
              className="review-bar__button"
              onClick={() => setAuthScreen(s.id)}
            >
              {s.label}
            </button>
          ))}
          <button type="button" className="review-bar__button" onClick={toggleTheme}>
            {theme === 'light' ? 'Dark' : 'Light'}
          </button>
          <button
            type="button"
            className="review-bar__button"
            onClick={() => setAuthScreen(null)}
          >
            Close
          </button>
        </div>
        ) : null}
      </>
    )
  }

  /**
   * ┌─ ⛔ SIGNED OUT LANDS ON THE AUTH FLOW. THE SCAFFOLD IS DELETED. ───────────┐
   * │ This branch used to render FOUNDATION's design-token review page - 81      │
   * │ lines of JSX with a "View Auth Screens" button - as the fallthrough for    │
   * │ every visitor who was not signed in. On the deployed product that was the  │
   * │ first thing anyone saw, and `App.tsx` opened by warning about it: *"the    │
   * │ cost of that was a login that appeared to fail."* It cost that twice.      │
   * │                                                                          │
   * │ ✅ THERE IS NO THIRD THING FOR THIS BRANCH TO BE. Signed in renders the    │
   * │ inbox above; not signed in renders the way in. A page between them existed │
   * │ only because FOUNDATION had no router.                                     │
   * │                                                                          │
   * │ ⚠️ THE TOKEN GALLERY IS NOT MOVED TO ANOTHER ROUTE, AND THAT IS A          │
   * │ DECISION: its `SEMANTIC_TOKENS` list was hand-maintained and showed **15   │
   * │ of the 44** semantic colour tokens `tokens.css` defines. A gallery that    │
   * │ shows a third of them reads as complete and is not - E4's class exactly.   │
   * │ `tokens.css` is the single source and four guards already assert it        │
   * │ (`check-colors`, `check-token-references`, `check-token-ladders`,          │
   * │ `check-theme-symmetry`). The gallery added nothing they assert and could   │
   * │ lie where they cannot.                                                     │
   * └────────────────────────────────────────────────────────────────────────────┘
   */
  return (
    <AuthFlow
      initial="signIn"
      onAuthenticated={() => {
        void fetchCurrentUserResult().then((r) => {
          setSession(r)
          setAuthScreen(null)
        })
      }}
    />
  )
}
